import React, { useState } from 'react'
import { Heart, Share2, Download, ExternalLink, Mail, Phone, MapPin } from 'lucide-react'

interface VisualArtistStudioProps {
  data: {
    artistName: string
    artistBio: string
    profileImage: string
    portfolio: Array<{
      id: string
      title: string
      category: string
      image: string
      description: string
      likes: number
      isLiked?: boolean
    }>
    commissionInfo: {
      title: string
      description: string
      pricing: string
      process: string[]
      contactEmail: string
    }
    socialLinks: {
      instagram: string
      twitter: string
      behance: string
      artstation: string
    }
    contactInfo: {
      email: string
      phone: string
      location: string
    }
  }
}

export default function VisualArtistStudio({ data }: VisualArtistStudioProps) {
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [likedItems, setLikedItems] = useState<Set<string>>(new Set())

  const categories = ['all', ...Array.from(new Set(data.portfolio.map(item => item.category)))]

  const filteredPortfolio = selectedCategory === 'all' 
    ? data.portfolio 
    : data.portfolio.filter(item => item.category === selectedCategory)

  const toggleLike = (itemId: string) => {
    const newLikedItems = new Set(likedItems)
    if (newLikedItems.has(itemId)) {
      newLikedItems.delete(itemId)
    } else {
      newLikedItems.add(itemId)
    }
    setLikedItems(newLikedItems)
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-900 via-black to-purple-900 text-white">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center">
        <div className="text-center max-w-4xl mx-auto px-6">
          {/* Profile Image */}
          <div className="mb-8">
            <img
              src={data.profileImage || '/api/placeholder/300/300'}
              alt={data.artistName}
              className="w-48 h-48 rounded-full mx-auto border-4 border-pink-400 shadow-2xl"
            />
          </div>
          
          {/* Artist Name */}
          <h1 
            className="text-6xl md:text-8xl font-bold mb-6"
            style={{ 
              fontFamily: 'Georgia, serif',
              textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
              background: 'linear-gradient(45deg, #EC4899, #8B5CF6, #06B6D4)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            {data.artistName}
          </h1>
          
          {/* Bio */}
          <p className="text-xl md:text-2xl text-pink-200 mb-8 max-w-2xl mx-auto leading-relaxed">
            {data.artistBio}
          </p>
          
          {/* Social Links */}
          <div className="flex justify-center gap-6 mb-8">
            {Object.entries(data.socialLinks).map(([platform, url]) => (
              <a
                key={platform}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center hover:bg-pink-500 hover:text-black transition-all duration-300"
              >
                {platform.charAt(0).toUpperCase()}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 
            className="text-4xl font-bold text-center mb-12"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            Portfolio
          </h2>
          
          {/* Category Filter */}
          <div className="flex justify-center gap-4 mb-12">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 rounded-full transition-colors ${
                  selectedCategory === category
                    ? 'bg-pink-500 text-black'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
              >
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </button>
            ))}
          </div>
          
          {/* Portfolio Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPortfolio.map((item) => (
              <div
                key={item.id}
                className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg overflow-hidden hover:bg-white/15 transition-all duration-300 group"
              >
                {/* Image */}
                <div className="aspect-square bg-gradient-to-br from-pink-500/20 to-purple-500/20 flex items-center justify-center relative overflow-hidden">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <ExternalLink className="w-8 h-8 text-white" />
                    </div>
                    <p className="text-white text-sm">{item.title}</p>
                  </div>
                  
                  {/* Hover Overlay */}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <div className="flex gap-4">
                      <button
                        onClick={() => toggleLike(item.id)}
                        className={`p-3 rounded-full transition-colors ${
                          likedItems.has(item.id)
                            ? 'bg-red-500 text-white'
                            : 'bg-white/20 text-white hover:bg-red-500'
                        }`}
                      >
                        <Heart className={`w-5 h-5 ${likedItems.has(item.id) ? 'fill-current' : ''}`} />
                      </button>
                      <button className="p-3 bg-white/20 text-white rounded-full hover:bg-blue-500 transition-colors">
                        <Share2 className="w-5 h-5" />
                      </button>
                      <button className="p-3 bg-white/20 text-white rounded-full hover:bg-green-500 transition-colors">
                        <Download className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Content */}
                <div className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xl font-semibold text-white">{item.title}</h3>
                    <span className="text-pink-400 text-sm bg-pink-500/20 px-2 py-1 rounded">
                      {item.category}
                    </span>
                  </div>
                  
                  <p className="text-pink-200 mb-4">{item.description}</p>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-pink-300">
                      {likedItems.has(item.id) ? item.likes + 1 : item.likes} likes
                    </span>
                    <button className="text-pink-400 hover:text-pink-300 transition-colors">
                      View Details →
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Commission Info Section */}
      <section className="py-20 px-6 bg-black/50">
        <div className="max-w-4xl mx-auto">
          <h2 
            className="text-4xl font-bold text-center mb-12"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            Commission Information
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Commission Details */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-8">
              <h3 className="text-2xl font-semibold text-white mb-4">{data.commissionInfo.title}</h3>
              <p className="text-pink-200 mb-6">{data.commissionInfo.description}</p>
              
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-white mb-2">Pricing</h4>
                <p className="text-pink-300">{data.commissionInfo.pricing}</p>
              </div>
              
              <div>
                <h4 className="text-lg font-semibold text-white mb-2">Process</h4>
                <ul className="space-y-2">
                  {data.commissionInfo.process.map((step, index) => (
                    <li key={index} className="text-pink-200 flex items-start gap-2">
                      <span className="text-pink-400 mt-1">{index + 1}.</span>
                      {step}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            
            {/* Contact Form */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-8">
              <h3 className="text-2xl font-semibold text-white mb-6">Get In Touch</h3>
              
              <form className="space-y-4">
                <div>
                  <label className="block text-white mb-2">Name</label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-pink-200 focus:ring-2 focus:ring-pink-400 focus:border-transparent"
                    placeholder="Your name"
                  />
                </div>
                
                <div>
                  <label className="block text-white mb-2">Email</label>
                  <input
                    type="email"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-pink-200 focus:ring-2 focus:ring-pink-400 focus:border-transparent"
                    placeholder="your@email.com"
                  />
                </div>
                
                <div>
                  <label className="block text-white mb-2">Project Type</label>
                  <select className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:ring-2 focus:ring-pink-400 focus:border-transparent">
                    <option value="">Select a project type</option>
                    <option value="illustration">Illustration</option>
                    <option value="portrait">Portrait</option>
                    <option value="logo">Logo Design</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-white mb-2">Message</label>
                  <textarea
                    rows={4}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-pink-200 focus:ring-2 focus:ring-pink-400 focus:border-transparent"
                    placeholder="Tell me about your project..."
                  />
                </div>
                
                <button
                  type="submit"
                  className="w-full bg-pink-500 text-black py-3 rounded-lg hover:bg-pink-600 transition-colors font-semibold"
                >
                  Send Message
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Info Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 
            className="text-4xl font-bold mb-12"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            Contact Information
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-6">
              <Mail className="w-8 h-8 text-pink-400 mx-auto mb-4" />
              <h3 className="text-white font-semibold mb-2">Email</h3>
              <p className="text-pink-200">{data.contactInfo.email}</p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-6">
              <Phone className="w-8 h-8 text-pink-400 mx-auto mb-4" />
              <h3 className="text-white font-semibold mb-2">Phone</h3>
              <p className="text-pink-200">{data.contactInfo.phone}</p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-6">
              <MapPin className="w-8 h-8 text-pink-400 mx-auto mb-4" />
              <h3 className="text-white font-semibold mb-2">Location</h3>
              <p className="text-pink-200">{data.contactInfo.location}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/20">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex justify-center gap-6 mb-6">
            {Object.entries(data.socialLinks).map(([platform, url]) => (
              <a
                key={platform}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-pink-200 hover:text-pink-400 transition-colors"
              >
                {platform.charAt(0).toUpperCase() + platform.slice(1)}
              </a>
            ))}
          </div>
          <p className="text-pink-200">
            © 2024 {data.artistName}. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  )
}
